import { Button, Collapse } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';

import { PropertyTable } from '#core/components/layout/PropertyTable.tsx';

import classes from './ToolDisplay.module.css';
import { LoadingShimmer } from '#plugin-chat/components/shared/LoadingShimmer.tsx';
import { useToolPart } from '#plugin-chat/hooks/useToolPart.ts';
import { useToolsMap } from '#plugin-chat/hooks/useToolsMap.ts';
import { renderInstructions, renderTranscript } from '#plugin-chat/lib/instructions/reconciler.ts';
import { parseResultEnvelope } from '#plugin-chat/lib/tool/result-envelope.ts';

interface ToolResultDetailsProps {
  toolName: string | undefined;
  args: Record<string, unknown> | undefined;
  result: unknown;
}

/**
 * Render the rendered tool-result message as React (HTML) for the
 * "Show details" panel.
 *
 * The same `message.result` formatter that powers the LLM transcript
 * also runs here — but the JSX it returns is rendered through standard
 * React DOM (so users see proper tables, lists, and code styling)
 * instead of through `renderInstructions` (which produces markdown text
 * for the LLM). This keeps the UI faithful to the prose the LLM sees.
 *
 * Failures: read `output.errorMessage` (already rendered at execute
 * time) verbatim.
 */
function ToolResultDetails({ toolName, args, result }: ToolResultDetailsProps) {
  const tools = useToolsMap();
  const message = toolName === undefined ? undefined : tools?.[toolName]?.message;
  const envelope = parseResultEnvelope(result);
  const transcript =
    toolName !== undefined && envelope.kind === 'success' && message?.transcript
      ? renderTranscript(
          message.transcript({ args: args ?? {}, outcome: { kind: 'result', output: result }, toolName }),
        )
          .map((turn) => `[${turn.role}]\n${turn.content}`)
          .join('\n\n')
      : undefined;

  return (
    <PropertyTable
      data={{
        input: args,
        ...(envelope.kind === 'success' &&
          message?.result && {
            message: renderInstructions(message.result({ args: args ?? {}, output: result })),
          }),
        ...(transcript !== undefined && { message: transcript }),
        output: result,
      }}
    />
  );
}

/**
 * Hover-revealed "Show details" affordance plus the collapsible
 * input/message/output inspection panel. Self-guarding: renders nothing
 * until the tool part carries a result.
 */
export function ToolDetails() {
  const { t } = useTranslation('chat');
  const [opened, { toggle }] = useDisclosure(false);
  const { args, result, toolName } = useToolPart();

  if (!result) {
    return null;
  }

  return (
    <>
      <div className={classes.actions}>
        <Button color="dark.3" fw="normal" h="lg" onClick={toggle} radius="xl" size="xs" variant="outline">
          {t('showDetails')}
        </Button>
      </div>

      <Collapse className={classes.responseObject} expanded={opened}>
        {opened && (
          <div className={classes.responseTable}>
            <Suspense fallback={<LoadingShimmer />}>
              <ErrorBoundary fallback={<PropertyTable data={{ error: true, input: args }} />}>
                <ToolResultDetails
                  args={args as Record<string, unknown> | undefined}
                  result={result}
                  toolName={toolName}
                />
              </ErrorBoundary>
            </Suspense>
          </div>
        )}
      </Collapse>
    </>
  );
}
